/**
 * The staff API contract for ticket sales campaigns: request validation (zod) and
 * response shapes, for `/api/v1/ticket-sales-campaigns`.
 */
import { z } from 'zod';

import {
  CAMPAIGN_DESCRIPTION_MAX_LENGTH,
  CAMPAIGN_NAME_MAX_LENGTH,
  CAMPAIGN_PERCENT_MAX,
  TicketSalesCampaignDiscountType,
  type CampaignDiscount,
  type TicketSalesCampaignStatus,
} from './ticket-sales-campaign.js';

/** The Postgres integer ceiling — a flat amount larger than this cannot be stored. */
const MAX_INT_CENTS = 2_147_483_647;

/** The money and timing fields whose combination has rules beyond each field's own. */
export interface CampaignRuleInput extends CampaignDiscount {
  startsAt: Date;
  endsAt: Date;
}

/** Field-keyed problems with a campaign's combined values; empty when it is valid. */
export function campaignRuleProblems(values: Partial<CampaignRuleInput>): Record<string, string> {
  const problems: Record<string, string> = {};
  if (
    values.discountType === TicketSalesCampaignDiscountType.PERCENT &&
    values.discountValue !== undefined &&
    values.discountValue > CAMPAIGN_PERCENT_MAX
  ) {
    problems.discountValue = `A percent discount must be between 1 and ${CAMPAIGN_PERCENT_MAX}`;
  }
  if (values.startsAt && values.endsAt && values.endsAt.getTime() <= values.startsAt.getTime()) {
    problems.endsAt = 'The end must be after the start';
  }
  return problems;
}

const instant = z
  .string()
  .datetime({ offset: true, message: 'Must be an ISO 8601 date and time' })
  .transform((value) => new Date(value));

const fields = {
  name: z.string().trim().min(1, 'Name is required').max(CAMPAIGN_NAME_MAX_LENGTH),
  // Blank means "no description", so the banner falls back to the discount text.
  description: z
    .string()
    .trim()
    .max(CAMPAIGN_DESCRIPTION_MAX_LENGTH)
    .nullable()
    .transform((value) => (value ? value : null)),
  discountType: z.nativeEnum(TicketSalesCampaignDiscountType),
  discountValue: z.number().int().positive().max(MAX_INT_CENTS),
  startsAt: instant,
  endsAt: instant,
  enabled: z.boolean(),
};

function addRuleIssues(values: Partial<CampaignRuleInput>, ctx: z.RefinementCtx): void {
  for (const [path, message] of Object.entries(campaignRuleProblems(values))) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message });
  }
}

export const ticketSalesCampaignCreateSchema = z
  .object({
    ...fields,
    description: fields.description.optional().transform((value) => value ?? null),
    enabled: fields.enabled.default(true),
  })
  .superRefine(addRuleIssues);

/**
 * A PATCH checks only the fields it carries; the service re-checks the merged
 * result. A new discount type must come with its amount, since the stored
 * number means something else under the other type (20% is not $0.20).
 */
export const ticketSalesCampaignPatchSchema = z
  .object({
    name: fields.name.optional(),
    description: fields.description.optional(),
    discountType: fields.discountType.optional(),
    discountValue: fields.discountValue.optional(),
    startsAt: fields.startsAt.optional(),
    endsAt: fields.endsAt.optional(),
    enabled: fields.enabled.optional(),
  })
  .refine((patch) => Object.values(patch).some((v) => v !== undefined), {
    message: 'Nothing to update',
  })
  .superRefine((patch, ctx) => {
    if (patch.discountType !== undefined && patch.discountValue === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['discountValue'],
        message: 'Send the amount along with the discount type',
      });
    }
    addRuleIssues(patch, ctx);
  });

export type TicketSalesCampaignCreateRequest = z.input<typeof ticketSalesCampaignCreateSchema>;
export type TicketSalesCampaignCreateInput = z.output<typeof ticketSalesCampaignCreateSchema>;
export type TicketSalesCampaignPatchRequest = z.input<typeof ticketSalesCampaignPatchSchema>;
export type TicketSalesCampaignPatchInput = z.output<typeof ticketSalesCampaignPatchSchema>;

export interface TicketSalesCampaignActor {
  id: string;
  name: string;
}

/** One campaign as the staff API returns it. Instants are UTC ISO strings. */
export interface TicketSalesCampaign extends CampaignDiscount {
  id: string;
  name: string;
  description: string | null;
  startsAt: string;
  endsAt: string;
  enabled: boolean;
  /** Computed by the server at request time. */
  status: TicketSalesCampaignStatus;
  createdBy: TicketSalesCampaignActor;
  createdAt: string;
  updatedAt: string;
}

/** The columns the staff list can be sorted by. Status is computed, so it is not one. */
export const TICKET_SALES_CAMPAIGN_SORT_FIELDS = [
  'createdAt',
  'startsAt',
  'endsAt',
  'name',
] as const;
export type TicketSalesCampaignSortField = (typeof TICKET_SALES_CAMPAIGN_SORT_FIELDS)[number];
export type TicketSalesCampaignSortOrder = 'asc' | 'desc';

export interface TicketSalesCampaignListResponse {
  campaigns: TicketSalesCampaign[];
  pagination: { limit: number; offset: number; total: number };
  sort: { field: TicketSalesCampaignSortField; order: TicketSalesCampaignSortOrder };
}

export interface TicketSalesCampaignResponse {
  campaign: TicketSalesCampaign;
}

export const TicketSalesCampaignEditEventType = {
  CREATED: 'CREATED',
  EDITED: 'EDITED',
} as const;
export type TicketSalesCampaignEditEventType =
  (typeof TicketSalesCampaignEditEventType)[keyof typeof TicketSalesCampaignEditEventType];

/** The fields a change-history row can name. */
export const TICKET_SALES_CAMPAIGN_AUDITED_FIELDS = [
  'name',
  'description',
  'discountType',
  'discountValue',
  'startsAt',
  'endsAt',
  'enabled',
] as const;
export type TicketSalesCampaignAuditedField = (typeof TICKET_SALES_CAMPAIGN_AUDITED_FIELDS)[number];

/** Instants are stored as UTC ISO strings; `before` is null on creation. */
export type TicketSalesCampaignFieldValue = string | number | boolean | null;

export type TicketSalesCampaignFieldChanges = Partial<
  Record<
    TicketSalesCampaignAuditedField,
    { before: TicketSalesCampaignFieldValue; after: TicketSalesCampaignFieldValue }
  >
>;

export interface TicketSalesCampaignEditEvent {
  id: string;
  eventType: TicketSalesCampaignEditEventType;
  actor: TicketSalesCampaignActor;
  changedAt: string;
  fieldChanges: TicketSalesCampaignFieldChanges;
}

/** Newest first. */
export interface TicketSalesCampaignHistoryResponse {
  events: TicketSalesCampaignEditEvent[];
}
